import { useMemo } from 'react';
import { useAuthStore, type UserRole } from '../lib/stores/auth-store';
import {
  type PermissionCategory,
  type DataVisibility,
  getRoleConfig,
  canViewNavItem,
  isNavItemReadonly,
  canViewData,
  isAdmin,
  canManageBilling,
  canManageUsers,
  canAccessSettings,
} from '../lib/config/permissions';
import { useOrganizationStore } from '../lib/stores/organization-store';
import { useUserStore } from '../lib/stores/user-store';

export function usePermissions() {
  const { organization } = useOrganizationStore()
  const { user } = useUserStore()

  // Get role from organization first (matches tenant context), fallback to user role
  const orgRoles = organization?.roles as UserRole[];
  const role = orgRoles?.[0] || (user?.role as UserRole) || 'admin';

  return useMemo(() => {
    const config = getRoleConfig(role);
    return {
      role,
      roleName: config.name,
      roleDescription: config.description,
      isAdmin: isAdmin(role),
      canManageBilling: canManageBilling(role),
      canManageUsers: canManageUsers(role),
      canAccessSettings: canAccessSettings(role),

      // Check if a nav item is visible
      canView: (category: PermissionCategory) => canViewNavItem(role, category),

      // Check if a nav item is readonly
      isReadonly: (category: PermissionCategory) => isNavItemReadonly(role, category),

      // Check data visibility
      canViewRevenue: canViewData(role, 'revenue'),
      canViewCosts: canViewData(role, 'costs'),
      canViewMargins: canViewData(role, 'margins'),
      canViewSettlements: canViewData(role, 'settlements'),
      canViewCustomerRates: canViewData(role, 'customerRates'),
      canViewDriverPay: canViewData(role, 'driverPay'),
      canViewCompanyFinancials: canViewData(role, 'companyFinancials'),

      // Get all visible nav categories
      visibleCategories: Object.entries(config.navigation)
        .filter(([, nav]) => nav?.visible)
        .map(([category]) => category as PermissionCategory),

      // Full config access
      config,
    };
  }, [role]);
}

// Hook for checking specific permission
export function useCanView(category: PermissionCategory): boolean {
  const { canView } = usePermissions();
  return canView(category);
}

// Hook for checking if user can see financial data
export function useCanViewFinancials() {
  const { canViewRevenue, canViewCosts, canViewMargins, canViewCompanyFinancials } = usePermissions();

  return {
    canViewRevenue,
    canViewCosts,
    canViewMargins,
    canViewAny: canViewRevenue || canViewCosts || canViewMargins || canViewCompanyFinancials,
  };
}

// Component wrapper for permission-based rendering
interface PermissionGateProps {
  children: React.ReactNode;
  category?: PermissionCategory;
  dataType?: keyof DataVisibility;
  requireAdmin?: boolean;
  fallback?: React.ReactNode;
}

export function usePermissionGate({
  category,
  dataType,
  requireAdmin,
}: Omit<PermissionGateProps, 'children' | 'fallback'>) {
  const permissions = usePermissions();

  if (requireAdmin && !permissions.isAdmin) {
    return { allowed: false, reason: 'Admin access required' };
  }

  if (category && !permissions.canView(category)) {
    return { allowed: false, reason: `No access to ${category}` };
  }

  if (dataType) {
    const key = `canView${dataType.charAt(0).toUpperCase() + dataType.slice(1)}` as keyof typeof permissions;
    if (!permissions[key]) {
      return { allowed: false, reason: `No access to ${dataType} data` };
    }
  }

  return { allowed: true, reason: null };
}
